import { ProctoringOption } from "@verity/proctoring-option/types";
import { CategoryOptionsListProps, OptionProps } from "../proctoringOptionsGrid";
import OptionBlock from "../optionBlock";
import { PROCTORING_OPTION_TEXT } from "../option-text";
import i18nPackage from "../../../i18n/i18n";
import { CheckBoxMenuSubItem } from "../option-menus/checkBoxMenuSubItem";
import styles from "./index.module.css";

function SignatureOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const notAllowed = !isOptionAllowed("uploadSignature");
	return (
		<OptionBlock
			name="uploadSignature"
			checked={option.uploadSignature ?? false}
			updateState={(checked) =>
				updateOption({
					...option,
					uploadSignature: checked,
				})
			}
			description={PROCTORING_OPTION_TEXT.uploadSignature.description}
			Icon={PROCTORING_OPTION_TEXT.uploadSignature.icon}
			contentHeader={PROCTORING_OPTION_TEXT.uploadSignature.header}
			disabled={disabled || notAllowed}
			fullDisabled={disabled || notAllowed}
			partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("uploadSignature")}
			disabledMessage={
				notAllowed
					? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled")
					: undefined
			}
			horizontalOrientation={horizontalOrientation}
		/>
	);
}

function IdVerificationOption<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	horizontalOrientation,
	getPartiallySupportedPlatforms,
}: OptionProps<T>) {
	const { idVerification, idVerificationEnableAi, idVerificationMode } = option;

	const notAllowed = !isOptionAllowed("idVerification");
	const menuItems = isOptionAllowed("idVerificationEnableAi") ? (
		<>
			<CheckBoxMenuSubItem
				label={i18nPackage.t("editQuizPage.configurationStep.verifyIdentityTSX.idVerificationEnableAi.label")}
				description={i18nPackage.t(
					"editQuizPage.configurationStep.verifyIdentityTSX.idVerificationEnableAi.description",
				)}
				checked={idVerificationEnableAi || false}
				onChange={(checked) => {
					updateOption({
						...option,
						idVerificationEnableAi: checked,
						idVerificationMode: checked ? "normal" : "off",
					});
				}}
				partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("idVerificationEnableAi")}
			/>
			{isOptionAllowed("idVerificationMode", "normal") &&
				isOptionAllowed("idVerificationMode", "strict") &&
				idVerificationEnableAi && (
					<div style={{ marginLeft: "25px" }}>
						<CheckBoxMenuSubItem
							label={i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.bypassModeText")}
							checked={idVerificationMode === "normal"}
							onChange={(checked) => {
								updateOption({
									...option,
									idVerificationMode: checked ? "normal" : "strict",
								});
							}}
						/>
					</div>
				)}
		</>
	) : null;

	return (
		<OptionBlock
			name="idVerification"
			checked={Boolean(idVerification)}
			updateState={(checked) =>
				updateOption({
					...option,
					idVerification: checked,
					idVerificationEnableAi: false,
					idVerificationMode: "off",
				})
			}
			description={PROCTORING_OPTION_TEXT.idVerification.description}
			Icon={PROCTORING_OPTION_TEXT.idVerification.icon}
			contentHeader={PROCTORING_OPTION_TEXT.idVerification.header}
			disabled={disabled || notAllowed}
			fullDisabled={disabled || notAllowed}
			disabledMessage={
				notAllowed ? i18nPackage.t("editQuizPage.configurationStep.proctoringOptionsGridTSX.instituteDisabled") : ""
			}
			horizontalOrientation={horizontalOrientation}
			partiallySupportedPlatforms={getPartiallySupportedPlatforms?.("idVerification")}
			{...(menuItems ? { menuItems } : {})}
		/>
	);
}

export default function VerifyIdentityOptions<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	isOptionAllowed,
	shouldShowOption,
	horizontalOrientation = false,
	getPartiallySupportedPlatforms,
}: CategoryOptionsListProps<T>) {
	return (
		<div className={styles.proctorSettingsGrid}>
			<IdVerificationOption
				option={option}
				updateOption={updateOption}
				disabled={disabled}
				isOptionAllowed={isOptionAllowed}
				horizontalOrientation={horizontalOrientation}
				getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
			/>
			{shouldShowOption("uploadSignature") && (
				<SignatureOption
					option={option}
					updateOption={updateOption}
					disabled={disabled}
					isOptionAllowed={isOptionAllowed}
					horizontalOrientation={horizontalOrientation}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			)}
		</div>
	);
}
